// Alles wat niet in de actieve pijplijn staat — T2.5.
//
// Dit scherm heette Archief en toonde één ding: Kavels met een `deleted_at`.
// Daarnaast stonden er twee groepen die nergens te zien waren:
//
//   * **Verloren.** De vierde fase heeft geen eigen route (de andere drie wel:
//     /acquisitie, /realisatie, /operatie). Gemeten op 12-09-2026 staan er
//     twee Kavels op `stage = 'verloren'`, en die kwamen op géén enkel scherm
//     voor — de Gauw-tabel filtert ze er expliciet uit en biedt geen knop om
//     ze terug te halen. Ze waren alleen via een directe URL te bereiken.
//
//   * **Slapend.** Nieuw in T2.5. Standaard verborgen op de fase-schermen, dus
//     zonder plek hier zou "verborgen" in de praktijk "weg" betekenen.
//
// Die drie staan nu naast elkaar, elk met hun eigen terugweg. Elke Kavel komt
// precies één keer voor: is hij gearchiveerd, dan staat hij daar; anders bij
// slapend; anders bij zijn fase. Geen dubbeling, geen gat.

import { useCallback, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Archive, RotateCcw, Trash2, Search, Moon, Sun, XCircle, HelpCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';
import { useKavelSlaap } from '@/hooks/useKavelSlaap';
import { partitioneerBuitenPijplijn, stadiumLabel } from '@/lib/chargingStages';

export default function ChargingArchief() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const { maakWakker } = useKavelSlaap();

  const basePath = location.pathname.startsWith('/crm') ? '/crm/charging' : '/charging';

  const { data: archivedLocations = [], isLoading } = useQuery({
    queryKey: ['charging-archived-locations'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('charging_locations')
        .select(`
          *,
          organizations(id, name)
        `)
        .not('deleted_at', 'is', null)
        .order('deleted_at', { ascending: false });

      if (error) throw error;
      return data || [];
    },
  });

  // Alles wat níét gearchiveerd is, in één query. `select('*')` omdat
  // `dormant_at` vóór migratie 084 niet bestaat en een smalle kolomlijst dan
  // met 42703 zou falen.
  const { data: levendeLocations = [] } = useQuery({
    queryKey: ['charging-locations', 'buiten-pijplijn'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('charging_locations')
        .select(`
          *,
          organizations(id, name)
        `)
        .is('deleted_at', null)
        .order('updated_at', { ascending: false });

      if (error) throw error;
      return data || [];
    },
  });

  const zoek = useCallback(
    (lijst: any[]) =>
      lijst.filter(
        (kavel: any) =>
          !searchQuery ||
          kavel.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          kavel.organizations?.name?.toLowerCase().includes(searchQuery.toLowerCase()),
      ),
    [searchQuery],
  );

  // De verdeling over de vier groepen staat in `lib/chargingStages.ts` en
  // wordt daar getoetst — met de assertie dat elke Kavel in precies één
  // partitie valt. Dit scherm tekent hem alleen, en zoekt erin.
  const partities = useMemo(
    () => partitioneerBuitenPijplijn([...(archivedLocations as any[]), ...(levendeLocations as any[])]),
    [archivedLocations, levendeLocations],
  );

  const gearchiveerd = useMemo(() => zoek(partities.gearchiveerd), [partities, zoek]);
  const slapend = useMemo(() => zoek(partities.slapend), [partities, zoek]);
  const verloren = useMemo(() => zoek(partities.verloren), [partities, zoek]);
  const nietIngedeeld = useMemo(() => zoek(partities.nietIngedeeld), [partities, zoek]);

  const restoreMutation = useMutation({
    mutationFn: async (locationId: string) => {
      const { data, error } = await supabase
        .from('charging_locations')
        .update({ deleted_at: null, updated_at: new Date().toISOString() })
        .eq('id', locationId)
        .select('id');
      if (error) throw error;
      // Een door RLS geblokkeerde UPDATE geeft geen fout maar nul rijen.
      if (!data || data.length === 0) {
        throw new Error('Herstellen: niets bijgewerkt — heb je hier schrijfrechten?');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['charging-archived-locations'] });
      queryClient.invalidateQueries({ queryKey: ['charging-locations'] });
      queryClient.invalidateQueries({ queryKey: ['charging-acquisitie-locations'] });
      queryClient.invalidateQueries({ queryKey: ['charging-realisatie-locations'] });
      queryClient.invalidateQueries({ queryKey: ['charging-operatie-locations'] });
      queryClient.invalidateQueries({ queryKey: ['gauw-tabel-data'] });
      toast({ title: 'Kavel hersteld' });
    },
    onError: (error: Error) =>
      toast({ title: 'Herstellen mislukt', description: error.message, variant: 'destructive' }),
  });

  const permanentDeleteMutation = useMutation({
    mutationFn: async (locationId: string) => {
      const { error } = await supabase
        .from('charging_locations')
        .delete()
        .eq('id', locationId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['charging-archived-locations'] });
      toast({ title: 'Kavel permanent verwijderd' });
    },
  });

  const KavelRegel = ({
    kavel,
    tijdstempel,
    acties,
    merk,
  }: {
    kavel: any;
    tijdstempel?: string | null;
    acties: React.ReactNode;
    merk: React.ReactNode;
  }) => (
    <Card key={kavel.id} className="p-4">
      <div className="flex items-center justify-between gap-4">
        <button
          type="button"
          className="min-w-0 flex-1 text-left"
          onClick={() => navigate(`${basePath}/locatie/${kavel.id}`)}
        >
          <div className="mb-1 flex flex-wrap items-center gap-2">
            <h3 className="font-semibold">{kavel.name}</h3>
            {merk}
            <Badge variant="secondary" className="text-xs">
              {stadiumLabel(kavel.stage)}
            </Badge>
          </div>
          {kavel.organizations && (
            <p className="mb-1 text-sm text-muted-foreground">{kavel.organizations.name}</p>
          )}
          {kavel.address && <p className="text-sm text-muted-foreground">{kavel.address}</p>}
          {tijdstempel && (
            <p className="mt-2 text-xs text-muted-foreground">{tijdstempel}</p>
          )}
        </button>
        <div className="flex shrink-0 gap-2">{acties}</div>
      </div>
    </Card>
  );

  const Sectie = ({
    titel,
    uitleg,
    icoon,
    aantal,
    leegTekst,
    children,
  }: {
    titel: string;
    uitleg: string;
    icoon: React.ReactNode;
    aantal: number;
    leegTekst: string;
    children: React.ReactNode;
  }) => (
    <section className="space-y-3">
      <div>
        <h2 className="flex items-center gap-2 text-xl font-semibold">
          {icoon}
          {titel}
          <Badge variant="secondary">{aantal}</Badge>
        </h2>
        <p className="text-sm text-muted-foreground">{uitleg}</p>
      </div>
      {aantal === 0 ? (
        <Card className="p-6 text-center text-sm text-muted-foreground">{leegTekst}</Card>
      ) : (
        <div className="space-y-3">{children}</div>
      )}
    </section>
  );

  return (
    <div className="container mx-auto p-6">
      <div className="space-y-8">
        <div>
          <h1 className="flex items-center gap-2 text-3xl font-bold">
            <Archive className="h-8 w-8" />
            Archief
          </h1>
          <p className="text-muted-foreground">
            Alles wat niet in de actieve pijplijn staat: gearchiveerd, slapend en verloren.
          </p>
        </div>

        <Card className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-muted-foreground" />
            <Input
              placeholder="Zoek in alle drie de groepen..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </Card>

        {isLoading ? (
          <div className="py-8 text-center">Laden...</div>
        ) : (
          <>
            <Sectie
              titel="Slapend"
              uitleg="Hier gebeurt nu niets. Niet gearchiveerd: de Kavel houdt zijn fase en stadium, en is met één klik terug in de lijst."
              icoon={<Moon className="h-5 w-5" />}
              aantal={slapend.length}
              leegTekst="Geen slapende Kavels."
            >
              {slapend.map((kavel: any) =>
                KavelRegel({
                  kavel,
                  tijdstempel: kavel.dormant_at
                    ? `Slaapt sinds ${format(new Date(kavel.dormant_at), 'dd MMM yyyy', { locale: nl })}`
                    : null,
                  merk: (
                    <Badge variant="outline" className="gap-1">
                      <Moon className="h-3 w-3" />
                      Slapend
                    </Badge>
                  ),
                  acties: (
                    <Button variant="outline" size="sm" onClick={() => maakWakker(kavel.id)}>
                      <Sun className="mr-1 h-4 w-4" />
                      Wakker maken
                    </Button>
                  ),
                }),
              )}
            </Sectie>

            <Sectie
              titel="Verloren"
              uitleg="De vierde fase. Deze Kavels staan niet in het archief — ze hebben alleen geen eigen scherm, dus ze staan hier."
              icoon={<XCircle className="h-5 w-5" />}
              aantal={verloren.length}
              leegTekst="Geen verloren Kavels."
            >
              {verloren.map((kavel: any) =>
                KavelRegel({
                  kavel,
                  tijdstempel: kavel.updated_at
                    ? `Laatst gewijzigd ${format(new Date(kavel.updated_at), 'dd MMM yyyy', { locale: nl })}`
                    : null,
                  merk: <Badge variant="destructive">Verloren</Badge>,
                  acties: (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`${basePath}/locatie/${kavel.id}`)}
                    >
                      Openen
                    </Button>
                  ),
                }),
              )}
            </Sectie>

            {nietIngedeeld.length > 0 && (
              <Sectie
                titel="Niet ingedeeld"
                uitleg="Het stadium van deze Kavels hoort bij geen van de vier fasen. Dat is een fout in de data, geen fase — open de Kavel en zet hem in het juiste stadium."
                icoon={<HelpCircle className="h-5 w-5" />}
                aantal={nietIngedeeld.length}
                leegTekst=""
              >
                {nietIngedeeld.map((kavel: any) =>
                  KavelRegel({
                    kavel,
                    tijdstempel: null,
                    merk: <Badge variant="outline">Onbekend stadium</Badge>,
                    acties: (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`${basePath}/locatie/${kavel.id}`)}
                      >
                        Openen
                      </Button>
                    ),
                  }),
                )}
              </Sectie>
            )}

            <Sectie
              titel="Gearchiveerd"
              uitleg="Uit de portefeuille. Telt nergens meer mee, maar is niet weg."
              icoon={<Archive className="h-5 w-5" />}
              aantal={gearchiveerd.length}
              leegTekst="Geen gearchiveerde Kavels."
            >
              {gearchiveerd.map((kavel: any) =>
                KavelRegel({
                  kavel,
                  tijdstempel: kavel.deleted_at
                    ? `Gearchiveerd op ${format(new Date(kavel.deleted_at), 'dd MMM yyyy HH:mm', { locale: nl })}`
                    : null,
                  merk: <Badge variant="outline">Gearchiveerd</Badge>,
                  acties: (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => restoreMutation.mutate(kavel.id)}
                        disabled={restoreMutation.isPending}
                      >
                        <RotateCcw className="mr-1 h-4 w-4" />
                        Herstel
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => {
                          if (confirm('Permanent verwijderen? Dit kan niet ongedaan worden!')) {
                            permanentDeleteMutation.mutate(kavel.id);
                          }
                        }}
                        disabled={permanentDeleteMutation.isPending}
                      >
                        <Trash2 className="mr-1 h-4 w-4" />
                        Definitief
                      </Button>
                    </>
                  ),
                }),
              )}
            </Sectie>
          </>
        )}
      </div>
    </div>
  );
}
